<template>
    <div v-if="isMobile" ref="navigatorEl" class="navigator z-1 sticky top-0 flex overflow-x-auto scrollbar-hide">
        <div
            v-for="(section, index) in sections"
            :key="index"
            ref="navigationItemRefs"
            :class="[`navigator__item-${index}`]"
            class="flex flex-col items-center"
        >
            <div
                :class="[
                    'text-center py-6 px-10 cursor-pointer whitespace-nowrap relative',
                    activeSection === index ? 'bg-darkerblue' : 'bg-darkergrey',
                ]"
                @click="selectSection(index)"
            >
                <p class="text-base font-semibold" :class="[activeSection === index ? 'text-white' : 'text-darkerblue']" v-html="section.title"></p>
            </div>

            <div
                v-if="activeSection === index"
                class="w-0 h-0 border-l-[15px] border-l-transparent border-t-[10px] border-t-darkerblue border-r-[15px] border-r-transparent"
            ></div>
        </div>
    </div>

    <div v-else ref="navigatorEl" class="navigator w-full z-1 shadow-sm sticky top-0 bg-white border-solid border-b-3 border-darkerblue px-8">
        <div class="container mx-auto relative pt-10 pb-20 px-0">
            <hr class="w-full h-1 bg-darkerblue absolute top-14 transform -translate-y-1/2" />

            <div class="flex justify-between items-center relative z-10">
                <div
                    v-for="(section, index) in sections"
                    :key="index"
                    ref="navigationItemRefs"
                    :class="[`navigator__item-${index}`]"
                    class="flex flex-col items-center cursor-pointer"
                    @click="selectSection(index)"
                >
                    <span
                        :class="[
                            'size-8 rounded-full border-3 border-solid border-darkerblue flex justify-center items-center ',
                            activeSection === index ? 'bg-darkerblue' : 'bg-darkgrey',
                        ]"
                    >
                    </span>

                    <p class="mt-3 text-base text-center  font-light absolute top-full w-max" v-html="section.title"></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

defineProps({
    sections: {
        type: Array,
        required: true,
    },
});

const activeSection = ref(0);
const screenWidth = ref(window.innerWidth);
const navigatorEl = ref(null);
const navigationItemRefs = ref([]);

const isMobile = computed(() => screenWidth.value < 768);

onMounted(() => {
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll);
});

onUnmounted(() => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll);
});

function onResize() {
    screenWidth.value = window.innerWidth;
}

function onScroll() {
    if (!navigatorEl.value) {
        return;
    }

    const scrollTop = window.scrollY;
    const navigatorHeight = navigatorEl.value.offsetHeight;
    const OFFSET = 50; // Section becomes active when scrolled to OFFSET px above the section

    const sectionOffsets = [];
    document.querySelectorAll('.case-study-section').forEach((section) => {
        sectionOffsets.push(section.offsetTop);
    });

    // If user is at the very bottom, force last section as active
    if (window.scrollY >= document.documentElement.scrollHeight - window.innerHeight) {
        activeSection.value = sectionOffsets.length - 1;
        return;
    }

    for (let index = sectionOffsets.length - 1; index >= 0; index--) {
        const sectionOffset = sectionOffsets[index];
        if (scrollTop + navigatorHeight >= sectionOffset - OFFSET) {
            activeSection.value = index;

            if (isMobile.value) {
                scrollNavigationItemIntoView(index);
            }

            break;
        }
    }
}

function selectSection(index) {
    const navigatorHeight = navigatorEl.value.offsetHeight;
    const section = document.querySelector(`.case-study-section[data-index="${index}"]`);
    const y = section.getBoundingClientRect().top + window.scrollY - navigatorHeight;

    window.scrollTo({
        top: y,
        behavior: 'smooth',
    });

    if (isMobile.value) {
        scrollNavigationItemIntoView(index);
    }
}

function scrollNavigationItemIntoView(index) {
    const navigatorItem = navigationItemRefs.value.find((el) => el.classList.contains(`navigator__item-${index}`));
    const itemOffset = navigatorItem.offsetLeft + navigatorItem.offsetWidth / 2;
    const scrollPosition = itemOffset - window.innerWidth / 2;

    navigatorEl.value.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
    });
}
</script>

<style scoped>
/* For Webkit-based browsers (Chrome, Safari and Opera) */
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

/* For IE, Edge and Firefox */
.scrollbar-hide {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
}
</style>
